import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { FaSearch, FaArrowLeft, FaExternalLinkAlt } from "react-icons/fa";

const API_URL = "https://step-in.onrender.com/api/government-jobs/";
const CATEGORY_API_URL = "https://step-in.onrender.com/api/government-jobs/category";
const STATE_API_URL = "https://step-in.onrender.com/api/government-jobs/state";

const GovernmentExams = ({ onBack }) => {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [states, setStates] = useState([]);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");

  useEffect(() => {
    fetch(API_URL)
      .then((res) => res.json())
      .then((data) => setData(data))
      .catch(console.error);

    fetch(CATEGORY_API_URL)
      .then((res) => res.json())
      .then((data) => setCategories(Array.isArray(data) ? data : [data]))
      .catch(console.error);

    fetch(STATE_API_URL)
      .then((res) => res.json())
      .then((data) => setStates(Array.isArray(data) ? data : [data]))
      .catch(console.error);
  }, []);

  const today = new Date().toDateString();

  const filtered = data
    .filter(
      (e) =>
        e.exam.toLowerCase().includes(search.toLowerCase()) ||
        e.organisation.toLowerCase().includes(search.toLowerCase())
    )
    .filter((e) => (!stateFilter || stateFilter === "All" ? true : e.state === stateFilter))
    .filter((e) => (!categoryFilter || categoryFilter === "All" ? true : e.category === categoryFilter))
    .filter((e) => (!statusFilter || statusFilter === "All" ? true : e.status === statusFilter))
    .sort((a, b) => new Date(a.last_date) - new Date(b.last_date));

  const statusStyle = {
    open: "bg-green-600",
    upcoming: "bg-blue-600",
    closed: "bg-gray-600",
  };

  return (
    <motion.section
      className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-gray-800 text-gray-200 px-4 sm:px-6 py-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <div className="max-w-7xl mx-auto">
        {/* Back */}
        <button
          onClick={onBack}
          className="flex items-center gap-2 mb-6 text-gray-400 hover:text-white"
        >
          <FaArrowLeft /> Back
        </button>

        <h1 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
          Government Exams & Recruitment
        </h1>

        {/* Filters */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          <div className="relative">
            <FaSearch className="absolute top-4 left-3 text-gray-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search exam or organisation"
              className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-xl focus:ring-2 focus:ring-gray-500"
            />
          </div>

          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-xl px-4 py-3"
          >
            <option value="" disabled>State</option>
            <option value="All">All States</option>
            {states.filter((s) => s).map((s, idx) => {
              const stateName = typeof s === 'string' ? s : s.state;
              if (!stateName) return null;
              return (
                <option key={idx} value={stateName}>
                  {stateName.charAt(0).toUpperCase() + stateName.slice(1)}
                </option>
              );
            })}
          </select>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-xl px-4 py-3"
          >
            <option value="" disabled>Category</option>
            <option value="All">All Categories</option>
            {categories.filter((cat) => cat).map((cat, idx) => {
              const categoryName = typeof cat === 'string' ? cat : cat.category;
              if (!categoryName) return null;
              return (
                <option key={idx} value={categoryName}>
                  {categoryName.charAt(0).toUpperCase() + categoryName.slice(1)}
                </option>
              );
            })}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-gray-900 border border-gray-700 rounded-xl px-4 py-3"
          >
            <option value="" disabled>Status</option>
            <option value="All">All Status</option>
            <option value="open">Open</option>
            <option value="upcoming">Upcoming</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {/* ================= MOBILE VIEW ================= */}
        <div className="sm:hidden space-y-4">
          {filtered.map((exam) => {
            const isToday =
              new Date(exam.last_date).toDateString() === today;

            return (
              <motion.div
                key={exam.id}
                whileHover={{ scale: 1.01 }}
                className="bg-gray-900 border border-gray-700 rounded-xl p-4"
              >
                <h3 className="font-semibold text-white">
                  {exam.exam}
                </h3>

                <p className="text-xs text-gray-400 mt-1">
                  {exam.organisation}
                </p>

                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`text-xs px-2 py-1 rounded text-white ${statusStyle[exam.status]}`}>
                    {exam.status}
                  </span>

                  {exam.state === "Assam" && (
                    <span className="text-xs px-2 py-1 bg-orange-600 rounded">
                      Assam
                    </span>
                  )}

                  {isToday && (
                    <span className="text-xs px-2 py-1 bg-red-600 rounded">
                      Last Date Today
                    </span>
                  )}
                </div>

                <div className="mt-3 text-sm text-gray-300">
                  Last Date: <span className="font-medium">{exam.last_date}</span>
                </div>

                <a
                  href={exam.apply}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 mt-4 text-sm text-white bg-gray-800 px-4 py-2 rounded-lg"
                >
                  Apply <FaExternalLinkAlt size={12} />
                </a>
              </motion.div>
            );
          })}
        </div>

        {/* ================= DESKTOP VIEW ================= */}
        <div className="hidden sm:block overflow-x-auto rounded-2xl border border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-900 text-gray-400">
              <tr>
                <th className="px-5 py-4 text-left">Exam</th>
                <th className="px-4 py-4 text-left">Organisation</th>
                <th className="px-4 py-4 text-left">Status</th>
                <th className="px-4 py-4 text-left">Last Date</th>
                <th className="px-4 py-4 text-left hidden md:table-cell">Exam Date</th>
                <th className="px-4 py-4 text-left">Apply</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((exam) => {
                const isToday =
                  new Date(exam.last_date).toDateString() === today;

                return (
                  <motion.tr
                    key={exam.id}
                    whileHover={{ scale: 1.01 }}
                    className="border-t border-gray-700 hover:bg-gray-900"
                  >
                    <td className="px-5 py-4 font-medium text-white">
                      {exam.exam}
                      {exam.state === "Assam" && (
                        <span className="ml-2 text-xs px-2 py-0.5 bg-orange-600 rounded">
                          Assam
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-4">{exam.organisation}</td>

                    <td className="px-4 py-4">
                      <span className={`px-2 py-1 text-xs rounded text-white ${statusStyle[exam.status]}`}>
                        {exam.status}
                      </span>
                    </td>

                    <td className={`px-4 py-4 ${isToday ? "text-red-400 font-semibold" : ""}`}>
                      {exam.last_date}
                      {isToday && " (Today)"}
                    </td>

                    <td className="px-4 py-4 hidden md:table-cell">
                      {exam.exam_date || "-"}
                    </td>

                    <td className="px-4 py-4">
                      <a
                        href={exam.apply}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-300 hover:text-white flex items-center gap-1"
                      >
                        Apply <FaExternalLinkAlt size={12} />
                      </a>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </motion.section>
  );
};

export default GovernmentExams;
